import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../components/Modal';
import {
  artifactsApi,
  ArtifactBundleDetailDTO,
  ArtifactDTO,
  ArtifactRequestOptions,
} from '../../utils/artifactsApi';
import { requestErrorDetail, showRequestError } from '../../utils/requestError';
import { focusField, formatPublishedAt } from '../../utils/artifactsUi';
import { ScheduleDateTimeInput } from '../ArtifactsPage/ScheduleDateTimeInput';
import { ArtifactItemsEditor, DraftArtifactItem, toCreateItem, validateDrafts } from '../ArtifactsPage/ArtifactItemsEditor';
import { isInFuture, localDateTimeToIso, parseTags } from '../ArtifactsPage/PublishBundleModal';
import pageStyles from '../ArtifactsPage/ArtifactsPage.module.scss';
import styles from './ArtifactBundlePage.module.scss';

interface BundleManagePanelProps {
  bundle: ArtifactBundleDetailDTO;
  artifacts: ArtifactDTO[];
  activeArtifact: ArtifactDTO | null;
  requestOptions: ArtifactRequestOptions;
  onChanged: () => void;
  onSelect: (artifactId: string) => void;
}

// ISO instant -> the datetime-local wall-clock form in the browser zone
function isoToLocalDateTime(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
}

// Publisher controls for one bundle: metadata / schedule editing, draft publish,
// archive / restore, and add / remove / reorder of the artifact tabs.
export function BundleManagePanel({
  bundle,
  artifacts,
  activeArtifact,
  requestOptions,
  onChanged,
  onSelect,
}: BundleManagePanelProps) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState(bundle.title);
  const [description, setDescription] = useState(bundle.description ?? '');
  const [tags, setTags] = useState(bundle.tags.join(', '));
  const [scheduledAt, setScheduledAt] = useState(bundle.scheduled_at ? isoToLocalDateTime(bundle.scheduled_at) : '');
  const [drafts, setDrafts] = useState<DraftArtifactItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const ids = useId();

  // Validation runs on submit, so a message would otherwise outlive the edit that fixes it
  useEffect(() => setError(null), [title, description, tags, scheduledAt, drafts]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      onChanged();
      return true;
    } catch (requestError) {
      showRequestError(requestError, t('Artifacts'));
      // the panel stays open on failure, so keep the reason visible after the toast goes;
      // a timeout or a dead connection has no detail to quote but still has to show
      setError(requestErrorDetail(requestError) ?? t('The change could not be saved'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openEdit = () => {
    setTitle(bundle.title);
    setDescription(bundle.description ?? '');
    setTags(bundle.tags.join(', '));
    setScheduledAt(bundle.scheduled_at ? isoToLocalDateTime(bundle.scheduled_at) : '');
    setError(null);
    setEditOpen(true);
  };

  const invalid = (message: string, fieldId: string) => {
    setError(message);
    focusField(fieldId);
  };

  const saveEdit = async () => {
    if (!title.trim()) return invalid(t('A bundle needs a title'), `${ids}-title`);
    if (bundle.status === 'DRAFT' && scheduledAt && !isInFuture(scheduledAt)) {
      return invalid(t('The schedule must be in the future'), `${ids}-schedule`);
    }
    const ok = await run(() =>
      artifactsApi.updateBundle(
        bundle.id,
        {
          title: title.trim(),
          description: description.trim() || null,
          tags: parseTags(tags),
          ...(bundle.status === 'DRAFT' ? { scheduled_at: scheduledAt ? localDateTimeToIso(scheduledAt) : null } : {}),
        },
        requestOptions,
      ),
    );
    if (ok) setEditOpen(false);
  };

  const openAdd = () => {
    setDrafts([]);
    setError(null);
    setAddOpen(true);
  };

  const saveAdd = async () => {
    const draft = drafts[0];
    if (!draft) return setError(t('No artifacts added yet'));
    const draftsError = validateDrafts(drafts, t);
    if (draftsError) return invalid(draftsError.message, draftsError.fieldId);
    const ok = await run(() => artifactsApi.addItem(bundle.id, toCreateItem(draft, artifacts.length), draft.file, requestOptions));
    if (ok) setAddOpen(false);
  };

  const removeActive = () => {
    if (!activeArtifact || !window.confirm(t('Remove this artifact from the bundle?'))) return;
    void run(() => artifactsApi.deleteItem(bundle.id, activeArtifact.id, requestOptions));
  };

  const moveActive = (delta: -1 | 1) => {
    if (!activeArtifact) return;
    const index = artifacts.findIndex((artifact) => artifact.id === activeArtifact.id);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= artifacts.length) return;
    // pin the tab to the moved artifact: without an explicit selection the page
    // falls back to the first one, which is no longer the artifact being moved
    onSelect(activeArtifact.id);
    void run(() => artifactsApi.updateItem(bundle.id, activeArtifact.id, { position: target }, requestOptions));
  };

  const archive = () => {
    if (!window.confirm(t('Archive this bundle? It disappears from the Hub but nothing is deleted.'))) return;
    void run(() => artifactsApi.archiveBundle(bundle.id, requestOptions));
  };

  const activeIndex = activeArtifact ? artifacts.findIndex((artifact) => artifact.id === activeArtifact.id) : -1;

  return (
    <div className={styles.manage}>
      <div className={styles.manageStatus}>
        {bundle.status === 'DRAFT' && <span className={styles.draftBadge}>{t('Draft')}</span>}
        {bundle.status === 'ARCHIVED' && <span className={styles.archivedBadge}>{t('Archived')}</span>}
        {bundle.status === 'DRAFT' && bundle.scheduled_at && (
          <span className={styles.scheduled}>
            {t('Scheduled for')} {formatPublishedAt(bundle.scheduled_at, i18n.language)}{' '}
            {new Date(bundle.scheduled_at).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <div className={styles.manageActions}>
        {bundle.status !== 'ARCHIVED' && (
          <button type="button" className={styles.manageButton} onClick={openEdit} disabled={busy}>
            {t('Edit bundle')}
          </button>
        )}
        {bundle.status === 'DRAFT' && (
          <button
            type="button"
            className={styles.manageButtonPrimary}
            onClick={() => void run(() => artifactsApi.publishDraft(bundle.id, requestOptions))}
            disabled={busy}
          >
            {t('Publish draft')}
          </button>
        )}
        {bundle.status !== 'ARCHIVED' && (
          <button type="button" className={styles.manageButton} onClick={openAdd} disabled={busy}>
            {t('Add artifact')}
          </button>
        )}
        {bundle.status !== 'ARCHIVED' && activeArtifact && (
          <>
            <button
              type="button"
              className={styles.manageButton}
              onClick={() => moveActive(-1)}
              disabled={busy || activeIndex <= 0}
            >
              {t('Move left')}
            </button>
            <button
              type="button"
              className={styles.manageButton}
              onClick={() => moveActive(1)}
              disabled={busy || activeIndex < 0 || activeIndex >= artifacts.length - 1}
            >
              {t('Move right')}
            </button>
            <button type="button" className={styles.manageButtonDanger} onClick={removeActive} disabled={busy}>
              {t('Remove artifact')}
            </button>
          </>
        )}
        {bundle.status === 'ARCHIVED' ? (
          <button
            type="button"
            className={styles.manageButtonPrimary}
            onClick={() => void run(() => artifactsApi.restoreBundle(bundle.id, requestOptions))}
            disabled={busy}
          >
            {t('Restore bundle')}
          </button>
        ) : (
          <button type="button" className={styles.manageButtonDanger} onClick={archive} disabled={busy}>
            {t('Archive bundle')}
          </button>
        )}
      </div>

      <Modal
        isVisible={editOpen}
        title={t('Edit bundle')}
        onClose={() => !busy && setEditOpen(false)}
        doNotCloseOnOutsideClick
        content={
          <div className={pageStyles.formContent}>
            <div className="form-element">
              <label htmlFor={`${ids}-title`}>{t('Title')}</label>
              <input
                id={`${ids}-title`}
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={busy}
                autoFocus
              />
            </div>
            <div className="form-element">
              <label htmlFor={`${ids}-description`}>{t('Description')}</label>
              <input
                id={`${ids}-description`}
                type="text"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={busy}
              />
            </div>
            <div className="form-element">
              <label htmlFor={`${ids}-tags`}>{t('Tags (comma separated)')}</label>
              <input
                id={`${ids}-tags`}
                type="text"
                value={tags}
                onChange={(event) => setTags(event.target.value)}
                disabled={busy}
              />
            </div>
            {bundle.status === 'DRAFT' && (
              <div className="form-element">
                <label htmlFor={`${ids}-schedule`}>{t('Schedule for')}</label>
                <div className={pageStyles.scheduleRow}>
                  <ScheduleDateTimeInput
                    id={`${ids}-schedule`}
                    value={scheduledAt}
                    onChange={setScheduledAt}
                    disabled={busy}
                  />
                  {scheduledAt && (
                    <button type="button" className="secondary" onClick={() => setScheduledAt('')} disabled={busy}>
                      {t('Clear schedule')}
                    </button>
                  )}
                </div>
              </div>
            )}
            {error && <div className={pageStyles.formError}>{error}</div>}
          </div>
        }
        footer={
          <>
            <button className="btn-primary" onClick={saveEdit} disabled={busy}>
              {t('Save')}
            </button>
            <button className="secondary" onClick={() => setEditOpen(false)} disabled={busy}>
              {t('Cancel')}
            </button>
          </>
        }
      />

      <Modal
        isVisible={addOpen}
        title={t('Add artifact')}
        onClose={() => !busy && setAddOpen(false)}
        doNotCloseOnOutsideClick
        className={pageStyles.publishModal}
        content={
          <div className={pageStyles.formContent}>
            <ArtifactItemsEditor items={drafts} onChange={setDrafts} single disabled={busy} />
            {error && <div className={pageStyles.formError}>{error}</div>}
          </div>
        }
        footer={
          <>
            <button className="btn-primary" onClick={saveAdd} disabled={busy}>
              {busy ? t('Publishing...') : t('Add')}
            </button>
            <button className="secondary" onClick={() => setAddOpen(false)} disabled={busy}>
              {t('Cancel')}
            </button>
          </>
        }
      />
    </div>
  );
}
